import { SelectionModel } from '@angular/cdk/collections';
import { CommonModule } from '@angular/common';
import { Component, OnInit, signal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ToastrService } from 'ngx-toastr';
import {
    catchError,
    combineLatest,
    debounceTime,
    distinctUntilChanged,
    filter,
    finalize,
    map,
    Observable,
    of,
    shareReplay,
    startWith,
    Subject,
    switchMap,
    tap,
} from 'rxjs';
import { BusinessDomainService } from '../../business-domain/business-domain.service';
import { ConfirmDialogComponent } from '../../common/confirm-dialog/confirm-dialog.component';
import { BusinessDomain } from '../../common/models/business-domain.model';
import { DataCategoriesService } from '../../data-categories/data-categories.service';
import { DataCategory } from '../../data-categories/models/data-category.model';
import { DatasetsService } from '../datasets.service';
import { DatasetInfo } from '../models/dataset-info.model';
import { ListDatasetsResponse } from '../models/list-datasets-response.model';
import { RunMode } from '../models/run-mode.model';

type DatasetsFilterForm = {
    businessDomain: FormControl<BusinessDomain | string | null>;
    runMode: FormControl<RunMode | null>;
    searchValue: FormControl<string>;
};

type AssignDataCategoryForm = {
    dataCategoryId: FormControl<number | null>;
};

@Component({
    selector: 'app-list-datasets',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatSelectModule,
        MatTableModule,
        MatPaginatorModule,
        MatProgressSpinnerModule,
        MatCheckboxModule,
        MatButtonModule,
        MatSidenavModule,
        MatIconModule,
        MatTooltipModule,
        MatAutocompleteModule,
        MatInputModule,
    ],
    templateUrl: './list-datasets.component.html',
    styleUrl: './list-datasets.component.scss',
    standalone: true,
})
export class ListDatasetsComponent implements OnInit {
    protected readonly pageSizeOptions = [10, 25, 50, 100];
    protected readonly defaultPageSize = 50;
    protected readonly minSearchLength = 3;

    protected businessDomains$!: Observable<BusinessDomain[]>;
    protected filteredBusinessDomains$!: Observable<BusinessDomain[]>;
    protected datasetsResponse$!: Observable<ListDatasetsResponse | null>;
    protected dataCategories$!: Observable<DataCategory[]>;
    protected displayedColumns: string[] = ['select', 'dataset', 'dataCategory', 'dbName', 'tableName', 'runMode'];
    protected filterForm: FormGroup<DatasetsFilterForm>;
    protected assignForm: FormGroup<AssignDataCategoryForm>;
    protected loadingResults = signal(false);
    protected assigning = signal(false);
    protected removingCategory = signal(false);
    protected filtersOpen = signal(false);
    protected readonly selection = new SelectionModel<string>(true, []);

    // Drives paginated fetches for the currently selected business domain
    private readonly pageEvents$ = new Subject<PageEvent>();
    private lastPageEvent: PageEvent = this.createInitialPageEvent();

    public constructor(
        private readonly businessDomainService: BusinessDomainService,
        private readonly datasetsService: DatasetsService,
        private readonly dataCategoriesService: DataCategoriesService,
        private readonly toastrService: ToastrService,
        private readonly dialog: MatDialog,
        private readonly fb: FormBuilder,
    ) {
        this.filterForm = this.fb.group({
            businessDomain: this.fb.control<BusinessDomain | string | null>(null),
            runMode: this.fb.control<RunMode | null>(null),
            searchValue: this.fb.nonNullable.control<string>(''),
        });
        this.assignForm = this.fb.group({
            dataCategoryId: this.fb.control<number | null>({ value: null, disabled: true }),
        });
    }

    public ngOnInit(): void {
        this.businessDomains$ = this.businessDomainService
            .listBusinessDomains({ returnAll: false })
            .pipe(shareReplay(1));

        // Shared source for both the autocomplete's search filter and the dataset fetch trigger below
        const businessDomainValue$ = this.filterForm.controls.businessDomain.valueChanges.pipe(
            startWith(this.filterForm.controls.businessDomain.value),
            shareReplay(1),
        );

        this.filteredBusinessDomains$ = combineLatest([this.businessDomains$, businessDomainValue$]).pipe(
            map(([domains, value]) => {
                const searchText = (typeof value === 'string' ? value : value?.name) ?? '';
                return domains.filter((domain) => domain.name.toLowerCase().includes(searchText.toLowerCase()));
            }),
        );

        // Only allow picking a data category once at least one dataset row is selected
        this.selection.changed.subscribe(() => {
            if (this.selection.isEmpty()) {
                this.assignForm.controls.dataCategoryId.disable();
            } else {
                this.assignForm.controls.dataCategoryId.enable();
            }
        });

        this.dataCategories$ = this.dataCategoriesService.listDataCategories().pipe(
            catchError(() => of([] as DataCategory[])),
            shareReplay(1),
        );

        this.datasetsResponse$ = combineLatest([
            businessDomainValue$.pipe(map((value) => this.getBusinessDomainId(value))),
            this.filterForm.controls.runMode.valueChanges.pipe(startWith(null)),
            this.filterForm.controls.searchValue.valueChanges.pipe(
                startWith(this.filterForm.controls.searchValue.value),
                debounceTime(500),
                distinctUntilChanged(),
                filter((value) => value.length === 0 || value.length >= this.minSearchLength),
            ),
        ]).pipe(
            switchMap(([businessDomainId, runMode, searchValue]) => {
                this.selection.clear();
                this.lastPageEvent = this.createInitialPageEvent();

                if (!businessDomainId) {
                    return of(null);
                }

                // Resetting to the first page whenever the business domain, run mode, or search text changes
                return this.pageEvents$.pipe(
                    startWith(this.lastPageEvent),
                    tap((pageEvent) => {
                        this.lastPageEvent = pageEvent;
                        this.loadingResults.set(true);
                    }),
                    switchMap((pageEvent) =>
                        this.datasetsService
                            .listDatasets(businessDomainId, {
                                pageNumber: pageEvent.pageIndex + 1,
                                pageSize: pageEvent.pageSize,
                                runMode: runMode ?? undefined,
                                searchValue: searchValue || undefined,
                            })
                            .pipe(
                                catchError(() => {
                                    this.toastrService.error('Failed to load datasets.', 'Load Failed');
                                    return of(null);
                                }),
                            ),
                    ),
                    tap(() => this.loadingResults.set(false)),
                );
            }),
        );
    }

    protected onPageChange(event: PageEvent): void {
        this.pageEvents$.next(event);
    }

    protected onRefresh(): void {
        this.pageEvents$.next(this.lastPageEvent);
    }

    protected onClearSearch(): void {
        this.filterForm.controls.searchValue.setValue('');
    }

    protected onRunModeFilterChange(runMode: RunMode, checked: boolean): void {
        this.filterForm.controls.runMode.setValue(checked ? runMode : null);
    }

    protected onClearFilters(): void {
        this.filterForm.controls.runMode.setValue(null);
    }

    protected getBusinessDomainName(value: BusinessDomain | string | null): string {
        if (!value) {
            return '';
        }

        return typeof value === 'string' ? value : value.name;
    }

    private getBusinessDomainId(value: BusinessDomain | string | null): string | null {
        return value && typeof value === 'object' ? value.id : null;
    }

    private createInitialPageEvent(): PageEvent {
        return { pageIndex: 0, pageSize: this.defaultPageSize, length: 0 };
    }

    protected isAllSelected(datasets: DatasetInfo[]): boolean {
        return (
            datasets.length > 0 && datasets.every((dataset) => this.selection.isSelected(this.getDatasetKey(dataset)))
        );
    }

    protected masterToggle(datasets: DatasetInfo[]): void {
        if (this.isAllSelected(datasets)) {
            datasets.forEach((dataset) => this.selection.deselect(this.getDatasetKey(dataset)));
        } else {
            datasets.forEach((dataset) => this.selection.select(this.getDatasetKey(dataset)));
        }
    }

    protected getDatasetKey(dataset: DatasetInfo): string {
        return dataset.dataset ?? '';
    }

    protected getCategoryName(dataCategoryId: number | null | undefined, categories: DataCategory[]): string {
        if (dataCategoryId == null) {
            return '';
        }

        return categories.find((category) => category.id === dataCategoryId)?.name ?? '';
    }

    protected onAssignDataCategory(): void {
        const businessDomainId = this.getBusinessDomainId(this.filterForm.controls.businessDomain.value);
        const dataCategoryId = this.assignForm.controls.dataCategoryId.value;
        const datasets = this.selection.selected;

        if (!businessDomainId || dataCategoryId == null || datasets.length === 0) {
            return;
        }

        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: 'Assign Data Category',
                message: `This will change the data category for ${datasets.length} selected dataset(s).`,
            },
        });

        dialogRef.afterClosed().subscribe((confirmed) => {
            if (!confirmed) {
                return;
            }

            this.assigning.set(true);
            this.dataCategoriesService
                .bulkUpdateDatasets(businessDomainId, dataCategoryId, { datasets })
                .pipe(finalize(() => this.assigning.set(false)))
                .subscribe({
                    next: () => {
                        this.toastrService.success('The data category was assigned.', 'Assign Succeeded');
                        this.selection.clear();
                        this.assignForm.reset();
                        this.pageEvents$.next(this.lastPageEvent);
                    },
                    error: () => {
                        this.toastrService.error('Failed to assign the data category.', 'Assign Failed');
                    },
                });
        });
    }

    protected onRemoveDataCategory(): void {
        const businessDomainId = this.getBusinessDomainId(this.filterForm.controls.businessDomain.value);
        const datasets = this.selection.selected;

        if (!businessDomainId || datasets.length === 0) {
            return;
        }

        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: 'Remove Data Category',
                message: `This will remove the data category from ${datasets.length} selected dataset(s).`,
                confirmLabel: 'Remove',
            },
        });

        dialogRef.afterClosed().subscribe((confirmed) => {
            if (!confirmed) {
                return;
            }

            this.removingCategory.set(true);
            this.dataCategoriesService
                // NOTE: assumes the backend treats dataCategoryId 0 as "clear category" — confirm with the API owner
                .bulkUpdateDatasets(businessDomainId, 0, { datasets })
                .pipe(finalize(() => this.removingCategory.set(false)))
                .subscribe({
                    next: () => {
                        this.toastrService.success('The data category was removed.', 'Remove Succeeded');
                        this.selection.clear();
                        this.pageEvents$.next(this.lastPageEvent);
                    },
                    error: () => {
                        this.toastrService.error('Failed to remove the data category.', 'Remove Failed');
                    },
                });
        });
    }
}
